import type { FileError } from '@ds/components/InputFileUpload/InputFileUpload.type'

import translations from '../translations/uploadErrors.translations'

import { FileUploadError, type UploadErrorReason } from './FileUploadError'

// Same shape as `I18nDefinition` from `@backmarket/nuxt-module-i18n`: render it
// with `useI18n()`.
export type UploadErrorMessage = {
  id: string
  defaultMessage: string
}

// The design-system `InputFileUpload` error types.
export type FileInputErrorType = FileError['type']

export type UploadErrorMessageOverrides = Partial<
  Record<UploadErrorReason | FileInputErrorType, UploadErrorMessage>
>

// A `Record` so that a new design-system error type fails the typecheck.
const FILE_INPUT_ERROR_TYPES = {
  WRONG_FILE_EXTENSION: true,
  MAX_FILE_SIZE_EXCEEDED: true,
  MAX_FILES_EXCEEDED: true,
  MAX_FILES_SIZE_EXCEEDED: true,
  MIN_FILE_SIZE_NOT_EXCEEDED: true,
} satisfies Record<FileInputErrorType, true>

function isFileInputErrorType(type: unknown): type is FileInputErrorType {
  return typeof type === 'string' && Object.hasOwn(FILE_INPUT_ERROR_TYPES, type)
}

function getMessageKey(error: unknown): UploadErrorReason | FileInputErrorType {
  if (error instanceof FileUploadError) return error.reason

  if (
    typeof error === 'object' &&
    error !== null &&
    'type' in error &&
    isFileInputErrorType(error.type)
  ) {
    return error.type
  }

  return 'SERVICE'
}

/**
 * Returns the message to show for a `FileUploadError`, a design-system file
 * input error, or any other error (the `SERVICE` message). A consumer can
 * replace the message of any reason or file input error with `overrides`.
 */
export function getUploadErrorMessage(
  error: unknown,
  overrides: UploadErrorMessageOverrides = {},
): UploadErrorMessage {
  const key = getMessageKey(error)

  return overrides[key] ?? translations[key]
}
